import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

type JwtPayload = { exp?: number };

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,  // Use the environment variable
  headers: {
    'Content-Type': 'application/json',
  },
});

// 🔐 Automatically add token to each request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  // Preflight: if token expired, block request
  if (token) {
    try {
      const { exp } = jwtDecode<JwtPayload>(token);
      const nowSec = Math.floor(Date.now() / 1000);
      if (!exp || exp <= nowSec) {
        // Expired - clear and redirect
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          const returnUrl = encodeURIComponent(window.location.href);
          window.location.replace(`/login?returnUrl=${returnUrl}`);
        }
        return Promise.reject(new Error('Session expired'));
      }
    } catch {
      // Invalid token - clear and redirect
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.replace(`/login?returnUrl=${returnUrl}`);
      }
      return Promise.reject(new Error('Invalid token'));
    }
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // NOTE: Removed Turnstile token - it's one-time use and shouldn't be sent on every request
  // Turnstile is only needed for specific sensitive operations (registration, contact forms, etc.)

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Global 401/403 handler
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const publicPaths = ['/home', '/', '/aboutus', '/departments', '/posts', '/register', '/login'];
    const isPublicPage = publicPaths.some(path => window.location.pathname === path || window.location.pathname.startsWith(path));
    
    if ((status === 401 || status === 403) && !isPublicPage) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.replace(`/login?returnUrl=${returnUrl}`);
      }
    }
    return Promise.reject(err);
  }
);

export default api;
